import { forwardRef, useEffect, useRef } from 'react'
import { Animated } from 'react-native'

import * as S from './select-trigger.styles'

import { useLayoutObserver } from '~/hooks'

import { Button } from '~/components/button'
import { Icon, type TIconProps } from '~/components/icon'

import { DISPLAY_NAME, useSelectContext } from '../../input-select'

import type { ScopedProps } from '../../input-select.types'

type SelectTriggerProps = Pick<
  React.ComponentPropsWithoutRef<typeof S.Trigger>,
  'children'
> & {
  leftIcon?: TIconProps['symbol']
  testID?: string
}

type SelectTriggerElementRef = React.ElementRef<typeof S.Trigger>

const ROTATE_DURATION = 200

const SelectTrigger = forwardRef<SelectTriggerElementRef, SelectTriggerProps>(
  (props, ref) => {
    const { __scopeSelect, children, leftIcon } =
      props as ScopedProps<SelectTriggerProps>

    const {
      size,
      isFocused,
      isDisabled,
      isReadOnly,
      isError,
      setTriggerToggle,
      selectedValue,
      setTriggerLayoutInfo,
      isClearButtonVisible,
    } = useSelectContext(DISPLAY_NAME, __scopeSelect)

    const triggerRef = useRef<React.ElementRef<typeof S.Trigger> | null>(null)
    const chevronRotate = useRef(new Animated.Value(0)).current

    const symbolSize = size === 'md' ? 'extra-small' : 'small'
    const clearButtonSize = size === 'md' ? 'md' : 'ml'

    const { measureLayout } = useLayoutObserver(triggerRef)

    const selectTriggerStates = {
      isFocused,
      isDisabled,
      isReadOnly,
      isError,
      isFilled: !!selectedValue,
    }

    function getChevronIconColor(
      isDisabled?: boolean,
      isReadOnly?: boolean
    ): TIconProps['color'] {
      if (isDisabled) return 'colorTextNeutralDisabled'
      if (isReadOnly) return 'colorTextNeutralReadonly'

      return 'colorTextNeutralDefault'
    }

    function getLeftIconColor(
      isDisabled?: boolean,
      isReadOnly?: boolean,
      isError?: boolean
    ): TIconProps['color'] {
      if (isDisabled) return 'colorTextNeutralDisabled'
      if (isReadOnly) return 'colorTextNeutralReadonly'
      if (isError) return 'colorTextDangerAlternative'

      return 'colorTextNeutralDefault'
    }

    function measureLayoutComponent() {
      if (isFocused) {
        measureLayout(({ width, height, pageX, pageY }) => {
          setTriggerLayoutInfo({
            x: pageX,
            y: pageY + height,
            width,
            height,
          })
        })
      }
    }

    // biome-ignore lint/correctness/useExhaustiveDependencies: <measureLayoutComponent> is called on layout change
    useEffect(() => {
      Animated.timing(chevronRotate, {
        toValue: isFocused ? 1 : 0,
        duration: ROTATE_DURATION,
        useNativeDriver: true,
      }).start()

      measureLayoutComponent()
      return () => {}
    }, [isFocused, chevronRotate])

    return (
      <S.Trigger
        {...props}
        {...selectTriggerStates}
        ref={triggerRef}
        size={size}
        onPress={() => setTriggerToggle('open')}
        collapsable={false}
        disabled={isDisabled || isReadOnly}
        onLayout={measureLayoutComponent}
      >
        <>
          {leftIcon && (
            <Icon
              symbol={isError ? 'rdicon-warning-circle' : leftIcon}
              size={symbolSize}
              color={getLeftIconColor(isDisabled, isReadOnly, isError)}
              testID="left-icon-trigger"
            />
          )}

          {children}

          {isClearButtonVisible && (
            <Button.Root
              variant="neutral-tertiary"
              size={clearButtonSize}
              testID="select-clear-button"
            >
              <Button.Icon symbol="rdicon-dismiss-circle-outline" />
            </Button.Root>
          )}

          <Animated.View
            style={{
              transform: [
                {
                  rotate: chevronRotate.interpolate({
                    inputRange: [0, 1],
                    outputRange: ['0deg', '180deg'],
                  }),
                },
              ],
            }}
          >
            <Icon
              symbol="rdicon-chevron-down"
              size={symbolSize}
              color={getChevronIconColor(isDisabled, isReadOnly)}
              testID="animated-icon-trigger"
            />
          </Animated.View>
        </>
      </S.Trigger>
    )
  }
)

SelectTrigger.displayName = 'SelectTrigger'

export { SelectTrigger }
